import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../auth/context/AuthContext';
import { FaSpinner, FaTrash, FaArrowLeft, FaUtensils } from 'react-icons/fa';
import { motion } from 'framer-motion';
import ConfirmationDialog from '../restaurants/ConfirmationDialog';

const CustomerPlaylistDetails = () => {
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { customerToken } = useAuth();
  const { playlistId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlaylistDetails = async () => {
      try {
        const response = await axios.get(`/api/customers/lists/${playlistId}`, {
          headers: { Authorization: `Bearer ${customerToken}` },
        });
        // console.log(response.data);
        setPlaylist(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch playlist details. Please try again.');
        setLoading(false);
      }
    };

    fetchPlaylistDetails();
  }, [customerToken, playlistId]);

  const truncateDescription = (description, maxLength = 100) => {
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength).trim()}...`;
  };

  const handleRemoveRestaurant = async (e, restaurantId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axios.delete(
        `/api/customers/lists/${playlistId}/restaurants/${restaurantId}`,
        {
          headers: { Authorization: `Bearer ${customerToken}` },
        }
      );
      setPlaylist((prevPlaylist) => ({
        ...prevPlaylist,
        restaurants: prevPlaylist.restaurants.filter(
          (r) => r._id !== restaurantId
        ),
      }));
    } catch (err) {
      setError('Failed to remove restaurant. Please try again.');
    }
  };

  const handleDeletePlaylist = async () => {
    try {
      await axios.delete(`/api/customers/${playlistId}`, {
        headers: { Authorization: `Bearer ${customerToken}` },
      });
      navigate('/playlists');
    } catch (err) {
      setError('Failed to delete playlist. Please try again.');
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getRandomColor = () => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <FaSpinner className='animate-spin text-4xl text-blue-500' />
      </div>
    );
  }

  if (error) {
    return <div className='text-red-500 text-center'>{error}</div>;
  }

  const isFavoriteList = playlist.name.toLowerCase() === 'favorites';

  return (
    <div className='container mx-auto px-4 py-8'>
      <div className='flex justify-between items-center mb-6'>
        <button
          onClick={() => navigate(-1)}
          className='flex items-center text-blue-500 hover:text-blue-700'
        >
          <FaArrowLeft className='mr-2' /> Back to Playlists
        </button>
        {!isFavoriteList && (
          <button
            onClick={() => setIsDeleteDialogOpen(true)}
            className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
          >
            Delete Playlist
          </button>
        )}
      </div>
      <h1 className='text-3xl font-bold mb-6'>{playlist.name}</h1>
      {playlist.restaurants.length === 0 ? (
        <div className='text-center py-10'>
          <FaUtensils className='text-6xl text-gray-300 mx-auto mb-4' />
          <p className='text-xl text-gray-500'>This playlist is empty.</p>
          <p className='text-gray-400 mt-2'>
            Add some restaurants to get started!
          </p>
        </div>
      ) : (
        <motion.div
          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {playlist.restaurants.map((restaurant) => (
            <motion.div
              key={restaurant._id}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link to={`/${restaurant._id}`} className='block'>
                <div className='bg-white shadow-md rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 relative'>
                  {restaurant.profileImage ? (
                    <img
                      src={restaurant.profileImage}
                      alt={restaurant.name}
                      className='w-full h-40 object-cover'
                    />
                  ) : (
                    <div
                      className='w-full h-40 flex items-center justify-center text-white text-3xl font-bold'
                      style={{ backgroundColor: getRandomColor() }}
                    >
                      {/* {getInitials(restaurant.name)} */}
                    </div>
                  )}
                  <div className='p-6'>
                    <h2 className='text-xl font-semibold mb-2'>
                      {restaurant.name}
                    </h2>
                    <p className='text-gray-600 mb-4'>
                      {/* {restaurant.description} */}
                      {truncateDescription(restaurant.description)}
                    </p>
                    <p className='text-gray-400 mb-4'>
                      {/* {restaurant.description} */}
                      {restaurant.address}
                    </p>
                    <span className='text-blue-500'>View Details</span>
                  </div>
                  <button
                    onClick={(e) => handleRemoveRestaurant(e, restaurant._id)}
                    className='absolute top-2 right-2 bg-white rounded-full p-2 shadow-md text-red-500 hover:text-red-700'
                  >
                    <FaTrash />
                  </button>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeletePlaylist}
        title='Delete Playlist'
        message='Are you sure you want to delete this playlist? This action cannot be undone.'
      />
    </div>
  );
};

export default CustomerPlaylistDetails;
// import React, { useState, useEffect } from 'react';
// import { useParams, Link, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { useAuth } from '../auth/context/AuthContext';
// import { FaSpinner, FaTrash, FaArrowLeft, FaUtensils } from 'react-icons/fa';
// import { motion } from 'framer-motion';

// const CustomerPlaylistDetails = () => {
//   const [playlist, setPlaylist] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const { customerToken } = useAuth();
//   const { playlistId } = useParams();
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchPlaylistDetails = async () => {
//       try {
//         const response = await axios.get(`/api/customers/lists/${playlistId}`, {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         });
//         setPlaylist(response.data);
//         setLoading(false);
//       } catch (err) {
//         setError('Failed to fetch playlist details. Please try again.');
//         setLoading(false);
//       }
//     };

//     fetchPlaylistDetails();
//   }, [customerToken, playlistId]);

//   const handleRemoveRestaurant = async (e, restaurantId) => {
//     e.stopPropagation();
//     try {
//       await axios.delete(
//         `/api/customers/lists/${playlistId}/restaurants/${restaurantId}`,
//         {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         }
//       );
//       setPlaylist((prevPlaylist) => ({
//         ...prevPlaylist,
//         restaurants: prevPlaylist.restaurants.filter(
//           (r) => r._id !== restaurantId
//         ),
//       }));
//     } catch (err) {
//       setError('Failed to remove restaurant. Please try again.');
//     }
//   };

//   if (loading) {
//     return (
//       <div className='flex justify-center items-center h-screen'>
//         <FaSpinner className='animate-spin text-4xl text-blue-500' />
//       </div>
//     );
//   }

//   if (error) {
//     return <div className='text-red-500 text-center'>{error}</div>;
//   }

//   return (
//     <div className='container mx-auto px-4 py-8'>
//       <button
//         onClick={() => navigate(-1)}
//         className='mb-6 flex items-center text-blue-500 hover:text-blue-700'
//       >
//         <FaArrowLeft className='mr-2' /> Back to Playlists
//       </button>
//       <h1 className='text-3xl font-bold mb-6'>{playlist.name}</h1>
//       {playlist.restaurants.length === 0 ? (
//         <div className='text-center py-10'>
//           <FaUtensils className='text-6xl text-gray-300 mx-auto mb-4' />
//           <p className='text-xl text-gray-500'>This playlist is empty.</p>
//           <p className='text-gray-400 mt-2'>
//             Add some restaurants to get started!
//           </p>
//         </div>
//       ) : (
//         <motion.div
//           className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           transition={{ duration: 0.5 }}
//         >
//           {playlist.restaurants.map((restaurant) => (
//             <motion.div
//               key={restaurant._id}
//               whileHover={{ scale: 1.03 }}
//               whileTap={{ scale: 0.98 }}
//             >
//               <Link to={`/${restaurant._id}`} className='block'>
//                 <div className='bg-white shadow-md rounded-lg p-6 hover:shadow-lg transition-shadow duration-300 relative'>
//                   <h2 className='text-xl font-semibold mb-2'>
//                     {restaurant.name}
//                   </h2>
//                   <p className='text-gray-600 mb-4'>{restaurant.description}</p>
//                   <div className='flex justify-between items-center'>
//                     {/* <span className='text-blue-500'>View Details</span> */}
//                     <button
//                       onClick={(e) => handleRemoveRestaurant(e, restaurant._id)}
//                       className='text-red-500 hover:text-red-700 absolute top-2 right-2'
//                     >
//                       <FaTrash />
//                     </button>
//                   </div>
//                 </div>
//               </Link>
//             </motion.div>
//           ))}
//         </motion.div>
//       )}
//     </div>
//   );
// };

// export default CustomerPlaylistDetails;
// import React, { useState, useEffect } from 'react';
// import { useParams, Link } from 'react-router-dom';
// import axios from 'axios';
// import { useAuth } from '../auth/context/AuthContext';
// import { FaSpinner, FaTrash } from 'react-icons/fa';

// const CustomerPlaylistDetails = () => {
//   const [playlist, setPlaylist] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const { customerToken } = useAuth();
//   const { playlistId } = useParams();

//   useEffect(() => {
//     const fetchPlaylistDetails = async () => {
//       try {
//         const response = await axios.get(`/api/customers/lists/${playlistId}`, {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         });
//         setPlaylist(response.data);
//         setLoading(false);
//       } catch (err) {
//         setError('Failed to fetch playlist details. Please try again.');
//         setLoading(false);
//       }
//     };

//     fetchPlaylistDetails();
//   }, [customerToken, playlistId]);

//   const handleRemoveRestaurant = async (restaurantId) => {
//     try {
//       await axios.delete(
//         `/api/customers/lists/${playlistId}/restaurants/${restaurantId}`,
//         {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         }
//       );
//       setPlaylist((prevPlaylist) => ({
//         ...prevPlaylist,
//         restaurants: prevPlaylist.restaurants.filter(
//           (r) => r._id !== restaurantId
//         ),
//       }));
//     } catch (err) {
//       setError('Failed to remove restaurant. Please try again.');
//     }
//   };

//   if (loading) {
//     return (
//       <div className='flex justify-center items-center h-screen'>
//         <FaSpinner className='animate-spin text-4xl text-blue-500' />
//       </div>
//     );
//   }

//   if (error) {
//     return <div className='text-red-500 text-center'>{error}</div>;
//   }

//   return (
//     <div className='container mx-auto px-4 py-8'>
//       <h1 className='text-3xl font-bold mb-6'>{playlist.name}</h1>
//       {playlist.restaurants.length === 0 ? (
//         <p>This playlist is empty.</p>
//       ) : (
//         <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
//           {playlist.restaurants.map((restaurant) => (
//             <div
//               key={restaurant._id}
//               className='bg-white shadow-md rounded-lg p-6'
//             >
//               <h2 className='text-xl font-semibold mb-2'>{restaurant.name}</h2>
//               <p className='text-gray-600 mb-4'>{restaurant.description}</p>
//               <div className='flex justify-between items-center'>
//                 <Link
//                   to={`/${restaurant._id}`}
//                   className='text-blue-500 hover:text-blue-700'
//                 >
//                   View Details
//                 </Link>
//                 <button
//                   onClick={() => handleRemoveRestaurant(restaurant._id)}
//                   className='text-red-500 hover:text-red-700'
//                 >
//                   <FaTrash />
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// };

// export default CustomerPlaylistDetails;
